import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Calendar, Clock, User2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/cmsConstants";

/*
 * Public blog listing at `/blog`.
 * Reuses landing-page color tokens for visual consistency.
 */
export default function BlogListPage() {
  const blogsQ = useQuery({
    queryKey: ["public-cms", "blogs"],
    queryFn: () => api.publicBlogs(),
    staleTime: 60_000,
  });
  const items = blogsQ.data?.items || [];
  const featured = items.filter((b) => b.fields?.featured);
  const rest = items.filter((b) => !b.fields?.featured);

  return (
    <div className="min-h-screen bg-white text-[#2c2c3e]">
      <header className="border-b border-[#e0e0e8] bg-white sticky top-0 z-10">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6 h-[64px] flex items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#6b6bb5] hover:underline"
            data-testid="blog-back-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="ml-auto">
            <Link
              to="/login"
              className="h-9 px-4 rounded-md text-sm font-medium inline-flex items-center hover:opacity-90"
              style={{ background: "#1a1a2e", color: "#fff" }}
            >
              Login
            </Link>
          </div>
        </div>
      </header>

      <main
        className="mx-auto max-w-[1200px] px-4 md:px-6 py-8 md:py-10"
        data-testid="blog-list-page"
      >
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#6b6bb5] font-medium mb-2">
            NDM Advisors · Blog
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1a1a2e] tracking-tight">
            Insights & Analysis
          </h1>
          <p className="mt-2 text-[14px] text-[#6b6bb5] max-w-2xl">
            Practitioner-written notes on tax, GST, corporate and secretarial
            compliance across India.
          </p>
        </div>

        {blogsQ.isLoading ? (
          <SkeletonGrid />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {featured.length > 0 && (
              <section className="mb-10">
                <SectionHeading title="Featured" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {featured.slice(0, 3).map((b) => (
                    <BlogListCard key={b.id} blog={b} large />
                  ))}
                </div>
              </section>
            )}
            {rest.length > 0 && (
              <section>
                {featured.length > 0 && <SectionHeading title="Latest" />}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {rest.map((b) => (
                    <BlogListCard key={b.id} blog={b} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function SectionHeading({ title }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <h2 className="text-lg font-bold text-[#1a1a2e]">{title}</h2>
      <span className="h-px flex-1 bg-[#e0e0e8]" />
    </div>
  );
}

export function BlogListCard({ blog, large }) {
  const f = blog.fields || {};
  return (
    <Link
      to={`/blog/${blog.slug}`}
      data-testid={`blog-card-${blog.slug}`}
      className="group flex flex-col gap-3 rounded-lg border border-[#e0e0e8] bg-white overflow-hidden hover:shadow-lg transition-shadow"
    >
      {f.coverImage ? (
        <img
          src={f.coverImage}
          alt=""
          className={`w-full object-cover ${large ? "h-48" : "h-40"}`}
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      ) : (
        <div
          className={`w-full ${large ? "h-48" : "h-40"} flex items-center justify-center`}
          style={{ background: "#f5f5f8", color: "#6b6bb5" }}
        >
          <span className="text-[11px] uppercase tracking-widest font-medium">
            NDM Advisors
          </span>
        </div>
      )}
      <div className="p-4 pt-1 flex flex-col gap-2">
        <div className="flex items-center flex-wrap gap-1.5">
          {(blog.category || []).slice(0, 2).map((c) => (
            <span
              key={c}
              className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
              style={{ background: "#2d2d7a", color: "#fff" }}
            >
              {c}
            </span>
          ))}
        </div>
        <h3
          className="text-[16px] font-bold leading-snug text-[#1a1a2e] group-hover:text-[#2d2d7a] transition-colors"
        >
          {blog.title}
        </h3>
        {f.metaDescription && (
          <p className="text-[13px] text-[#2c2c3e] line-clamp-2 leading-snug">
            {f.metaDescription}
          </p>
        )}
        <div className="flex items-center gap-3 text-[11px] text-[#888] mt-1">
          {f.author && (
            <span className="inline-flex items-center gap-1">
              <User2 className="h-3 w-3" /> {f.author}
            </span>
          )}
          {f.publishDate && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {formatDate(f.publishDate)}
            </span>
          )}
          {f.readingTime && (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> {f.readingTime} min
            </span>
          )}
        </div>
        <span className="mt-1 inline-flex items-center gap-1 text-[12px] font-medium text-[#6b6bb5] group-hover:gap-1.5 transition-all">
          Read more <ArrowRight className="h-3 w-3" />
        </span>
      </div>
    </Link>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-72 rounded-lg border border-[#e0e0e8] bg-[#f5f5f8] animate-pulse"
        />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="rounded-lg border border-dashed border-[#e0e0e8] bg-[#f5f5f8] p-10 text-center"
      data-testid="blog-empty"
    >
      <div className="text-[13px] text-[#6b6bb5] font-medium mb-1">
        No blog posts yet
      </div>
      <div className="text-[12px] text-[#888]">
        New articles will appear here as they are published.
      </div>
    </div>
  );
}
